import type { AppEnv } from '@stopal/shared/app-env'

/**
 * One deployment: the consumer hosts and the business hosts serving the SAME
 * environment.
 *
 * Pairing them is the whole point. The product gate has to answer "where is
 * Business for the host I am standing on", and that answer differs per
 * environment — staging must send a business user to staging, never to
 * production. Declaring the pair is what makes the answer derivable; two flat
 * per-brand lists cannot express it.
 *
 * `env` names the tier. `'local'` is expressed by having no row at all, not by
 * a value — but the type admits it, so nothing here may classify a tier by
 * being NOT production. See `TIER_OFFERS_PRODUCTION_LINK`.
 *
 * `business` is a non-empty tuple when present, so `business[0]` — the host a
 * cross-origin redirect targets — is type-safe and can never be a host the
 * brand lookup fails to recognise. Omitting it is meaningful: that deployment
 * has no business host, so the gate promotes in place instead of redirecting.
 *
 * Its own module, with no runtime imports, because `vite.config.ts` reads it to
 * build the dev server's `allowedHosts`. Vite restarts the dev server whenever
 * a config dependency changes, so declaring this inside `brand.ts` dragged
 * `safe-storage.ts` into that set and made editing an unrelated localStorage
 * helper cost a full restart.
 */
export interface Deployment {
  env: AppEnv
  consumer: readonly [string, ...string[]]
  business?: readonly [string, ...string[]]
}

export const DEPLOYMENTS: readonly Deployment[] = [
  {
    env: 'production',
    consumer: ['stopal.app', 'www.stopal.app'],
    business: ['stopalbusiness.com', 'www.stopalbusiness.com'],
  },
  {
    env: 'staging',
    // `stopal-web-staging.fly.dev` is the Fly app's own hostname, which serves
    // byte-identical responses to `staging.stopal.app`. Declaring it is not
    // cosmetic: an undeclared host resolves to the consumer default by accident
    // rather than by decision, and `businessHostFor` answered `null` there — so
    // a business user on it was promoted in place instead of being sent to the
    // staging business host that exists.
    //
    // Production's twin, `stopal-web.fly.dev`, is knowingly NOT declared and
    // carries that same defect. Declaring it would change a production
    // redirect, which is a bigger decision than this one.
    consumer: ['staging.stopal.app', 'stopal-web-staging.fly.dev'],
    business: ['staging.stopalbusiness.com'],
  },
]

/**
 * Does a tier offer a link to its production twin?
 *
 * A `Record` over the whole of `AppEnv`, not `env !== 'production'`, so a tier
 * added to `APP_ENVS` fails the build until someone decides. Classifying by the
 * COMPLEMENT of production is the shape `20-architecture.md` names as worse
 * than the hand-typed copy it replaces: a new tier is by construction absent
 * from the excluded set, so it lands in the complement and is silently
 * labelled "Staging" — with a link to production — on whatever host declares
 * it. That regression was live for one commit.
 */
export const TIER_OFFERS_PRODUCTION_LINK: Record<AppEnv, boolean> = {
  local: false,
  staging: true,
  production: false,
}

/** Every host this build knows about, in declaration order. */
export const DEPLOYMENT_HOSTS: readonly string[] = DEPLOYMENTS.flatMap((d) => [
  ...d.consumer,
  ...(d.business ?? []),
])

/**
 * The hosts serving one tier.
 *
 * Exported for the things that must agree with this table but cannot reach it
 * through a brand — the dev server's `allowedHosts` and the e2e host-resolver
 * map. Both were hand-copied lists before, which is two more places to forget.
 */
export function deploymentHostsFor(env: AppEnv): readonly string[] {
  return DEPLOYMENTS.filter((d) => d.env === env).flatMap((d) => [
    ...d.consumer,
    ...(d.business ?? []),
  ])
}
